import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { Close } from './Glyph';
import { OnHandIcon } from './brand/OnHandLogo';

const ToastContext = createContext(null);

// How long a message stays, and how long it takes to leave (guide 10.4).
const SHOW_MS = 4000;
const LEAVE_MS = 360;

/*
  **The four marks, on white, in a solid square of the state's own colour.**
  Changed 10 September 2026 for job 25, to Group H's drawing.

  They used to be little coloured discs on a tinted square, which was a shape
  nothing else in OnHand uses. These are drawn the way every other mark in the
  app is drawn: one line weight, round caps, white on the colour that says what
  kind of thing this is. The same four colours as a count state.
*/
const ICONS = {
  success: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="var(--on-action)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  ),
  error: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="var(--on-action)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  warning: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="var(--on-action)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6v7" /><path d="M12 17.5h.01" />
    </svg>
  ),
  info: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="var(--on-action)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 11v6" /><path d="M12 7.5h.01" />
    </svg>
  ),
};

// Support "title | message" syntax or plain message
function parseMessage(m) {
  if (typeof m === 'string' && m.includes(' | ')) {
    const idx = m.indexOf(' | ');
    return { title: m.slice(0, idx), msg: m.slice(idx + 3) };
  }
  return { title: null, msg: m };
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', opts = {}) => {
    const id = Date.now() + Math.random();
    // Leaves after 4 seconds, over 360ms (rebrand step 3, guide 10.4 and 11.8;
    // it was 5 seconds and 250ms).
    const timer = setTimeout(() => {
      setToasts(t => t.map(x => x.id === id ? { ...x, removing: true } : x));
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), LEAVE_MS);
    }, SHOW_MS);
    setToasts(t => [...t, { id, message, type, timer, removing: false, mark: opts.mark || null }]);
  }, []);

  function dismissOne(id) {
    setToasts(t => t.map(x => x.id === id ? { ...x, removing: true } : x));
    setTimeout(() => {
      setToasts(t => {
        const toast = t.find(x => x.id === id);
        if (toast) clearTimeout(toast.timer);
        return t.filter(x => x.id !== id);
      });
    }, LEAVE_MS);
  }

  /*
    **Held still on purpose.** This object used to be rebuilt on every render,
    and this component re-renders every time a message appears or fades. Any
    screen that lists `toast` among the things it depends on therefore reloaded
    itself each time. The Warehouses screen does, and a message renders three
    times as it comes and goes, so **one "Saved" cost three full reloads of all
    320 locations, all 13 people and the referral sources**, and a person saw the
    list flash back to "Loading" and refill, twice more after the message had
    gone.

    `addToast` is already held still, so this has nothing that changes in it.
  */
  const toast = useMemo(() => ({
    /* A success can carry the logo's success drop in place of the tick
       (rebrand step 2, guide 3.7): `toast.success(message, { mark: 'delivered' })`
       for a count submitted or an order saved. Every other message is as it was. */
    success: (m, opts) => addToast(m, 'success', opts),
    error:   (m) => addToast(m, 'error'),
    info:    (m) => addToast(m, 'info'),
    warning: (m) => addToast(m, 'warning'),
  }), [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/*
        **The stack announces itself, since 11 September 2026, phase 6.**

        Until that day these messages had no `role` at all, so **every pop-up in
        OnHand was silent to a screen reader**: a count submitted, a save that
        failed, a request sent, all of them invisible to somebody not looking at
        that corner of the screen. The brand guide's section 13 asks for it by
        name: "Toasts `role="status"`, errors `role="alert"`."

        **The live region is the STACK, not each message.** A region announced
        only when it already exists can say what arrives in it; one created at
        the same moment as its content is not reliably read at all. So the stack
        is always in the page, empty most of the time.

        **Two regions, because an error is not a notice.** `alert` interrupts
        whatever is being read; `status` waits its turn. A count that saved can
        wait. A save that did not cannot.
      */}
      <div className="toast-stack">
        <div className="toast-region" role="status" aria-live="polite">
          {toasts.filter(t => t.type !== 'error').map(t => renderToast(t, dismissOne))}
        </div>
        <div className="toast-region" role="alert" aria-live="assertive">
          {toasts.filter(t => t.type === 'error').map(t => renderToast(t, dismissOne))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

/*
  One message. Lifted out of the provider on 11 September 2026 so the two live
  regions cannot draw it differently.

  **The close control is a real button now.** It was a `<div>` holding the
  character U+2715, which meant it could not be reached by keyboard, had no
  name for a screen reader, and drew at whatever weight the reader's fallback
  face gave it. The message itself is still clickable, because it always was and
  reaching for the cross on a phone is fiddly.
*/
function renderToast(t, dismissOne) {
  const { title, msg } = parseMessage(t.message);
  return (
    <div
      key={t.id}
      className={`toast-notif toast-notif-${t.type}${t.removing ? ' toast-removing' : ''}`}
      onClick={() => dismissOne(t.id)}
    >
      {t.mark === 'delivered'
        ? <div className="toast-notif-icon is-mark" aria-hidden="true"><OnHandIcon size={28} animate="delivered" decorative /></div>
        : <div className="toast-notif-icon" aria-hidden="true">{ICONS[t.type]}</div>}
      <div className="toast-notif-body">
        {title && <div className="toast-notif-title">{title}</div>}
        <div className={title ? 'toast-notif-msg' : 'toast-notif-title'}>{msg}</div>
      </div>
      <button
        type="button"
        className="toast-notif-close"
        aria-label="Dismiss this message"
        onClick={e => { e.stopPropagation(); dismissOne(t.id); }}
      >
        <Close size={14} />
      </button>
      <div className="toast-notif-progress"></div>
    </div>
  );
}

export const useToast = () => useContext(ToastContext);
